import { useEffect, useState } from 'react';

import PubSub from 'pubsub-js';

import { DXP_FOOD_ACCGRP_DOCUMENTS, DXP_RED_CARPET_USER_GROUP, REQUEST_SAMPLE_PERMISSION } from '../../constants';
import { logger } from '../../logger';
import { getAppConfig, getServiceConfig } from '../../utilities';
import { Get } from '../../utilities/api-wrapper';
import { isUserExternal } from '../../utilities/auth';
import { updateSessionStorage } from '../../utilities/common';
import { BRAZIL } from '../../utilities/constants';
import useIsMobile from '../../utilities/useIsMobile';
import { DXP_FOOD_ACCGRP_PRODUCTS } from '../constants';

export default function useNavigationData(headerConfig = null, userGroups = null) {
  const [categories, setCategories] = useState({});
  const [navigationData, setNavigationData] = useState(null);

  const isAccountAssociated = updateSessionStorage.getItem('isAccountAssociated');

  const { country = '' } = getAppConfig('locale');

  const { isMobile } = useIsMobile();

  const fetchDataCategories = async () => {
    const { url } = getServiceConfig('getGlobalNavProduct');
    Get(url, true)
      .then(responseJSON => {
        const { data } = responseJSON;
        setCategories(data);
        const response = (data && data.CT001) || {
          items: []
        };
        PubSub.publish('categoriesData', response);
      })
      .catch(err => {
        PubSub.publish('xhrError', {
          text: `Unable to fetch categories`
        });
        logger.error('Error in getting categories', err);
        setCategories({});
      });
  };

  const isBrazilProductDocuments = item => country.toLowerCase() === BRAZIL && item.id === 'productdocuments';

  const isRequestSampleWithoutPermission = (item, groups) =>
    item.type?.toLowerCase() === 'requestsample' && !groups.includes(REQUEST_SAMPLE_PERMISSION.toLowerCase());

  const filterNavigationByPermission = (permissions, groups, items = []) =>
    items.filter(item => {
      if (item.id === 'CT001' && !isMobile) {
        Object.defineProperty(item, 'isProductsLoader', {
          value: true
        });
      }
      if (isBrazilProductDocuments(item)) return false;
      if (isRequestSampleWithoutPermission(item, groups)) return false;

      if (item.type === 'customerrequests') {
        if (isAccountAssociated) {
          return isUserExternal() && groups.includes(DXP_FOOD_ACCGRP_DOCUMENTS.toLowerCase());
        }
        return !groups.includes(DXP_RED_CARPET_USER_GROUP.toLowerCase());
      }

      if (permissions[item.id]) {
        if (item.items) {
          const filteredItems = filterNavigationByPermission(permissions, groups, item.items);
          if (filteredItems.length) {
            const updatedItem = item;
            updatedItem.items = filteredItems;
            return updatedItem;
          }
        }

        if (permissions[item.id].roles.find(role => groups.includes(role.toLowerCase()))) {
          const updatedItem = item;
          delete updatedItem.items;
          return updatedItem;
        }
      }
      return false;
    });

  const filterOutCategoriesIfNotCT600 = productCategories => {
    const isEmployee = productCategories.length && productCategories[0].type.toLowerCase() === 'employee';
    if (isEmployee) {
      return productCategories.map(category => ({
        ...category,
        items: category.items.length
          ? category.items.filter(subCategory => subCategory.id && subCategory.id.toLowerCase() === 'ct600')
          : []
      }));
    }
    return productCategories.length ? productCategories.filter(category => category.id.toLowerCase() === 'ct600') : [];
  };

  const updateNavLinkData = (response, obj) => {
    const data = response;
    return data.map((currentItem, ind) => {
      if (!currentItem.link.href) {
        data[ind].link.href = obj.link.href.concat('/', currentItem.id);
      }
      if (currentItem.items) {
        currentItem.items.forEach((subChild, keyIndex) => {
          if (!subChild.link.href) {
            data[ind].items[keyIndex].link.href = obj.link.href.concat('/', currentItem.id, '/', subChild.id);
          }

          if (subChild.items) {
            subChild.items.forEach((levelThreeChild, thirdIndex) => {
              if (!levelThreeChild.link.href) {
                data[ind].items[keyIndex].items[thirdIndex].link.href = obj.link.href.concat(
                  '/',
                  currentItem.id,
                  '/',
                  subChild.id,
                  '/',
                  levelThreeChild.id
                );
              }
            });
          }
        });
      }
      return currentItem;
    });
  };

  const includeChildItemsToNav = navItems => {
    const isProductsPermission = navItems.find(item => {
      const {
        link: { href = '' }
      } = item;
      return href.indexOf('products') !== -1;
    });
    const isCategories = Object.keys(categories);

    const id = isCategories[0];
    const { items } = categories[id];
    const childItemsToBeAppended = filterOutCategoriesIfNotCT600(items);
    const productItems = isMobile
      ? isProductsPermission?.items.filter(i => i.id !== 'loader')
      : isProductsPermission?.items;
    const isRequestSample = productItems || [];
    const requestSampleIncluded = [...isRequestSample, ...childItemsToBeAppended];
    const childItemsAreAppended = updateNavLinkData(
      requestSampleIncluded,
      navItems.find(item => item.id === id)
    );
    const childItemsIncludedToMainNavItems = navItems.map(item => {
      if (item.id === id) {
        return { id: item.id, items: [...childItemsAreAppended], link: item.link, type: item.type };
      }
      return { ...item };
    });
    setNavigationData(childItemsIncludedToMainNavItems);
  };

  useEffect(() => {
    if (userGroups && userGroups.length) {
      if (userGroups.includes(DXP_FOOD_ACCGRP_PRODUCTS)) {
        fetchDataCategories(); // Categories API call
      } else {
        setCategories({});
      }
    } else if (userGroups && !userGroups.length) {
      // if null: lds ring will be active, when truthy like [] or {}, lds ring will be turned off
      setNavigationData([]);
    }
  }, [userGroups]);

  useEffect(() => {
    if (Object.keys(categories).length && navigationData?.length) {
      includeChildItemsToNav([...navigationData]);
    }
  }, [categories]);

  useEffect(() => {
    if (headerConfig && userGroups && isAccountAssociated !== '') {
      const { permissions, items } = headerConfig;
      const navFilteredByPermission = filterNavigationByPermission(permissions, userGroups, items);
      if (isMobile) {
        navFilteredByPermission.forEach(item => {
          if (item.id === 'CT001') {
            item.items.push({ id: 'loader', link: { href: '' }, isProductsLoader: true, type: 'loader' });
          }
        });
      }
      setNavigationData(navFilteredByPermission);
    }
  }, [headerConfig, userGroups, isAccountAssociated]);

  return { navigationData };
}
